/**
 * Academy (spec 011, secao 3, e spec 035): os cursos do cliente, a aula com video e o "continuar de onde parou". O
 * cadeado, o progresso e a liberacao por plano vem do backend (ADR-001); a tela so mostra. O video toca por um token
 * assinado de curta duracao, pedido a cada aula, e a tela manda a cada 15 s e ao sair os trechos que tocaram de fato:
 * e a soma deles que conclui a aula, nao a posicao (035, 6.3).
 */
import { AnteriorEProxima, Aviso, BarraDeProgresso, Botao, BotaoLink, CabecalhoDePagina, Cartao, CampoTextoLongo, Carregando, EsqueletoDeCartao, IconeCadeado, IconeVoltar, LinkInterno, ListaDeAulas, Pilha, Recolhivel, Selo, formatarDataHora, mensagemDe, type EstadoDaAula } from "@mtc/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router";
import { useApi } from "../acoes";

/** A mensagem que o backend escreveu; sem ela, uma frase que diz o que fazer. */
const erroDe = (err: unknown) => mensagemDe(err, "Não deu certo agora. Tente de novo em instantes.");

type ProgressoDaAula = { percentual: number; posicao_segundos: number; concluida: boolean };
type AulaNaLista = { id: string; titulo: string; descricao: string | null; duracao_segundos: number | null; tem_video: boolean; liberada: boolean; progresso: ProgressoDaAula };
type ModuloNaLista = { id: string; titulo: string; descricao: string | null; liberado: boolean; planos: string[]; aulas: AulaNaLista[]; concluidas: number };
export type Academy = { acesso: { pode: boolean; motivo: string | null }; continuar: { curso: string; modulo: string; aula_id: string; titulo: string; posicao_segundos: number } | null; cursos: { id: string; titulo: string; descricao: string | null; modulos: ModuloNaLista[] }[] };
type Pergunta = { id: string; segundo: number; texto: string; respostas: { id: string; texto: string; criada_em: string }[]; respondida_em: string | null; resolvida_em: string | null; criada_em: string };
type Trecho = [number, number];
type Aula = { id: string; titulo: string; descricao: string | null; texto: string | null; duracao_segundos: number | null; tem_video: boolean; liberada: boolean; motivo: string | null; curso: { titulo: string }; modulo: { titulo: string }; progresso: ProgressoDaAula; perguntas: Pergunta[] };

const minutos = (s: number | null) => (s ? `${Math.max(1, Math.round(s / 60))} min` : null);
const relogio = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

// ---------------------------------------------------------------- lista

/** A Academy do cliente (a lista, o "continuar" e os vizinhos da aula saem daqui; o Inicio usa a mesma consulta). */
export function useAcademy() {
  const { ler } = useApi();
  return useQuery({ queryKey: ["academy"], queryFn: () => ler<Academy>("/v1/me/academy") });
}

/** O estado da aula pro icone: o backend diz se concluiu e quanto viu; a tela so escolhe o desenho. */
function estadoDa(a: AulaNaLista, liberado: boolean): EstadoDaAula {
  if (!liberado || !a.liberada) return "fechada";
  if (a.progresso.concluida) return "concluida";
  return a.progresso.percentual > 0 ? "andamento" : "nova";
}
const apoioDa = (a: AulaNaLista) => [minutos(a.duracao_segundos), !a.progresso.concluida && a.progresso.percentual > 0 ? `${a.progresso.percentual}%` : null].filter(Boolean).join(", ");

export function PaginaAcademy() {
  const q = useAcademy();
  if (q.isLoading) return <Pilha espaco={5}><CabecalhoDePagina titulo="Academy" /><EsqueletoDeCartao linhas={2} barra rotulo="Abrindo a Academy" /><EsqueletoDeCartao linhas={3} /></Pilha>;
  if (q.isError) return <Aviso tipo="erro">{erroDe(q.error)}</Aviso>;
  const d = q.data!;
  const aulaDoContinuar = d.continuar ? d.cursos.flatMap((c) => c.modulos.flatMap((m) => m.aulas)).find((a) => a.id === d.continuar!.aula_id) : undefined;
  return (
    <Pilha espaco={6}>
      <CabecalhoDePagina titulo="Academy" descricao={!d.acesso.pode && d.acesso.motivo ? d.acesso.motivo : undefined} />
      {d.continuar && (
        <Cartao>
          <Pilha espaco={3}>
            <span className="mtc-rotulo">Continue de onde parou</span>
            <strong style={{ fontSize: "var(--t-lg)", fontWeight: 400, overflowWrap: "anywhere" }}>{d.continuar.titulo}</strong>
            <span className="mtc-texto-2" style={{ fontSize: "var(--t-sm)" }}>{d.continuar.curso}, {d.continuar.modulo}, parou em {relogio(d.continuar.posicao_segundos)}</span>
            {aulaDoContinuar && <BarraDeProgresso percentual={aulaDoContinuar.progresso.concluida ? 100 : aulaDoContinuar.progresso.percentual} rotulo={`Quanto você viu de ${d.continuar.titulo}`} mostrarNumero />}
            <div><BotaoLink href={`/academy/aulas/${d.continuar.aula_id}`} variante="primario">Continuar</BotaoLink></div>
          </Pilha>
        </Cartao>
      )}
      {!d.cursos.length && <Cartao><p className="mtc-texto-2" style={{ margin: 0 }}>Nenhum curso publicado ainda. Assim que a equipe publicar, ele aparece aqui.</p></Cartao>}
      {d.cursos.map((c) => <CartaoDoCurso key={c.id} c={c} continuarId={d.continuar?.aula_id ?? null} />)}
    </Pilha>
  );
}

/**
 * Um curso em cartao (spec 038): o progresso do curso (aulas concluidas dos modulos liberados) e os modulos, que abrem
 * e fecham. Nasce aberto o que esta por fazer (liberado e nao concluido) e o modulo da aula de "continuar".
 */
function CartaoDoCurso({ c, continuarId }: { c: Academy["cursos"][number]; continuarId: string | null }) {
  const liberados = c.modulos.filter((m) => m.liberado);
  const total = liberados.reduce((n, m) => n + m.aulas.length, 0);
  const feitas = liberados.reduce((n, m) => n + m.concluidas, 0);
  const pct = total ? Math.round((feitas / total) * 100) : 0;
  return (
    <section className="mtc-cartao mtc-curso" aria-label={c.titulo}>
      <div className="mtc-curso__cabeca">
        <h2 className="mtc-curso__titulo">{c.titulo}</h2>
        {c.descricao && <p className="mtc-texto-2" style={{ margin: 0 }}>{c.descricao}</p>}
        {total > 0 && (
          <>
            <BarraDeProgresso percentual={pct} rotulo={`Progresso em ${c.titulo}`} />
            <span className="mtc-texto-2" style={{ fontSize: "var(--t-sm)" }}>{total} {total === 1 ? "aula" : "aulas"}, {pct}% concluído</span>
          </>
        )}
      </div>
      <div className="mtc-curso__modulos">
        {c.modulos.map((m) => {
          const completo = m.aulas.length > 0 && m.concluidas === m.aulas.length;
          const aberto = (m.liberado && !completo) || m.aulas.some((a) => a.id === continuarId);
          return (
            <Recolhivel
              key={m.id}
              abertoInicial={aberto}
              titulo={m.titulo}
              apoio={m.descricao ?? undefined}
              lateral={m.liberado ? <Selo tom={completo ? "sucesso" : "neutro"}>{m.concluidas} de {m.aulas.length}</Selo> : <Selo tom="alerta"><IconeCadeado tamanho={12} /> {m.planos.length ? m.planos.join(" ou ") : "Plano ativo"}</Selo>}
            >
              <ListaDeAulas aulas={m.aulas.map((a) => ({ id: a.id, titulo: a.titulo, href: m.liberado ? `/academy/aulas/${a.id}` : undefined, apoio: apoioDa(a) || undefined, estado: estadoDa(a, m.liberado) }))} />
            </Recolhivel>
          );
        })}
      </div>
    </section>
  );
}

/** A aula de antes e a de depois no mesmo curso, na ordem dos modulos (so as que a pessoa pode abrir). */
function useVizinhas(aulaId: string) {
  const q = useAcademy();
  const curso = q.data?.cursos.find((c) => c.modulos.some((m) => m.aulas.some((a) => a.id === aulaId)));
  if (!curso) return { anterior: null, proxima: null };
  const abertas = curso.modulos.filter((m) => m.liberado).flatMap((m) => m.aulas.filter((a) => a.liberada));
  const i = abertas.findIndex((a) => a.id === aulaId);
  const ir = (a: AulaNaLista | undefined) => (a ? { href: `/academy/aulas/${a.id}`, titulo: a.titulo } : null);
  return i < 0 ? { anterior: null, proxima: null } : { anterior: ir(abertas[i - 1]), proxima: ir(abertas[i + 1]) };
}

// ---------------------------------------------------------------- aula

export function PaginaAula() {
  const { id = "" } = useParams();
  const { ler, postar } = useApi();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["academy", "aula", id], queryFn: () => ler<Aula>(`/v1/me/academy/aulas/${id}`) });
  const [pergunta, setPergunta] = useState("");
  const [enviada, setEnviada] = useState(false);
  const [erroDaPergunta, setErroDaPergunta] = useState<string>();
  const a = q.data;
  const perguntar = useMutation({
    mutationFn: (texto: string) => postar(`/v1/me/academy/aulas/${id}/perguntas`, { segundo: Math.round(posicao.current), texto }),
    onSuccess: async () => { setPergunta(""); setEnviada(true); setErroDaPergunta(undefined); await qc.invalidateQueries({ queryKey: ["academy", "aula", id] }); },
    onError: (e) => setErroDaPergunta(erroDe(e)),
  });
  const concluir = useMutation({
    mutationFn: () => postar(`/v1/me/academy/aulas/${id}/concluir`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["academy"] }),
  });
  const resolver = useMutation({
    mutationFn: (perguntaId: string) => postar(`/v1/me/academy/perguntas/${perguntaId}/resolver`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["academy", "aula", id] }),
    onError: (e) => setErroDaPergunta(erroDe(e)),
  });
  const posicao = useRef(0);
  const vizinhas = useVizinhas(id);
  useEffect(() => { posicao.current = a?.progresso.posicao_segundos ?? 0; }, [a?.id]);
  if (q.isLoading) return <Carregando texto="Abrindo a aula" />;
  if (q.isError || !a) return <Pilha espaco={4}><Voltar /><Aviso tipo="erro">{q.error ? erroDe(q.error) : "Aula não encontrada."}</Aviso></Pilha>;
  return (
    <Pilha espaco={5}>
      <Voltar />
      <header style={{ display: "grid", gap: "var(--e-2)" }}>
        <h1 className="mtc-titulo mtc-cabecalho__titulo">{a.titulo}</h1>
        <p className="mtc-cabecalho__descricao">{a.curso.titulo} · {a.modulo.titulo}</p>
        {a.descricao && <p className="mtc-cabecalho__descricao">{a.descricao}</p>}
      </header>
      {!a.liberada ? (
        <Aviso tipo="alerta">{a.motivo}</Aviso>
      ) : (
        <>
          {a.tem_video && <Player aulaId={a.id} inicio={a.progresso.posicao_segundos} duracao={a.duracao_segundos} aoAndar={(s) => (posicao.current = s)} />}
          {a.texto && <Cartao><div style={{ whiteSpace: "pre-line", lineHeight: 1.6 }}>{a.texto}</div></Cartao>}
          <Cartao>
            <Pilha espaco={4}>
              <div className="mtc-linha mtc-linha--entre" style={{ gap: "var(--e-3)" }}>
                <span className="mtc-rotulo">Seu progresso</span>
                {a.progresso.concluida ? <Selo tom="sucesso">Concluída</Selo> : <Selo>{a.progresso.percentual}%</Selo>}
              </div>
              {/* Concluida e concluida: a barra fica cheia mesmo que o video tenha tocado menos (spec 038). */}
              <BarraDeProgresso percentual={a.progresso.concluida ? 100 : a.progresso.percentual} rotulo="Progresso nesta aula" mostrarNumero />
              {!a.progresso.concluida && (
                <div>
                  <Botao variante="secundario" onClick={() => concluir.mutate()} carregando={concluir.isPending}>Marcar como concluída</Botao>
                </div>
              )}
            </Pilha>
          </Cartao>
          <Cartao>
            <Pilha espaco={4}>
              <span className="mtc-rotulo">Dúvidas nesta aula</span>
              {a.perguntas.map((p) => (
                <div key={p.id} style={{ display: "grid", gap: 4 }}>
                  <div className="mtc-linha mtc-linha--entre" style={{ gap: "var(--e-2)", alignItems: "flex-start" }}>
                    <span style={{ fontWeight: 500 }}>Aos {relogio(p.segundo)}: {p.texto}</span>
                    {p.resolvida_em && <Selo tom="sucesso">Resolvida</Selo>}
                  </div>
                  {p.respostas.map((r) => (
                    <span key={r.id} className="mtc-texto-2" style={{ fontSize: "var(--t-sm)" }}>Resposta da equipe em {formatarDataHora(r.criada_em)}: {r.texto}</span>
                  ))}
                  {!p.respostas.length && !p.resolvida_em && (
                    <span className="mtc-texto-2" style={{ fontSize: "var(--t-sm)" }}>Enviada em {formatarDataHora(p.criada_em)}. A equipe responde por aqui e avisa no sino.</span>
                  )}
                  {!p.resolvida_em && (
                    <div>
                      <button type="button" className="mtc-link" disabled={resolver.isPending} onClick={() => resolver.mutate(p.id)}>{p.respostas.length ? "Resolveu minha dúvida" : "Não preciso mais, marcar como resolvida"}</button>
                    </div>
                  )}
                </div>
              ))}
              {enviada && <Aviso tipo="sucesso">Pergunta enviada. A equipe responde por aqui e você recebe um aviso.</Aviso>}
              {erroDaPergunta && <Aviso tipo="erro">{erroDaPergunta}</Aviso>}
              <CampoTextoLongo rotulo="Perguntar à equipe" valor={pergunta} aoMudar={(v) => { setPergunta(v); setEnviada(false); }} maxLength={2000} placeholder="Escreva sua dúvida sobre esta aula." dica="A pergunta vai com o ponto do vídeo em que você está." />
              <Botao onClick={() => perguntar.mutate(pergunta.trim())} carregando={perguntar.isPending} disabled={pergunta.trim().length < 5}>Enviar pergunta</Botao>
            </Pilha>
          </Cartao>
        </>
      )}
      <AnteriorEProxima anterior={vizinhas.anterior} proxima={vizinhas.proxima} />
    </Pilha>
  );
}

function Voltar() {
  return <LinkInterno href="/academy" className="mtc-voltar"><IconeVoltar tamanho={16} /> Academy</LinkInterno>;
}

/**
 * O player do Cloudflare Stream: o backend assina um token curto por aula. O SDK do Stream diz onde o video esta; a
 * tela junta o tempo que tocou sem salto num trecho e, a cada 15 s e ao sair, manda os trechos novos. Arrastar a barra
 * fecha o trecho e abre outro onde parou: o que foi pulado nao conta (035, 6.3). Sem o SDK (rede ruim, bloqueador), o
 * video toca e a aula conclui pelo botao "marcar como concluida".
 */
function Player({ aulaId, inicio, duracao, aoAndar }: { aulaId: string; inicio: number; duracao: number | null; aoAndar: (s: number) => void }) {
  const { ler, postar } = useApi();
  const iframe = useRef<HTMLIFrameElement>(null);
  const ultimoEnvio = useRef(0);
  const aberto = useRef<Trecho | null>(null);
  const prontos = useRef<Trecho[]>([]);
  const q = useQuery({ queryKey: ["academy", "video", aulaId], queryFn: () => ler<{ iframe_url: string }>(`/v1/me/academy/aulas/${aulaId}/video`), retry: false, staleTime: 20 * 60_000 });

  useEffect(() => {
    if (!q.data || !iframe.current) return;
    let player: { addEventListener: (e: string, f: () => void) => void; currentTime: number } | null = null;
    let parar = false;
    /** Manda a posicao e os trechos desde o ultimo envio. O trecho aberto continua aberto, a partir de onde parou. */
    const enviar = (segundos: number) => {
      const t = aberto.current;
      const trechos = [...prontos.current, ...(t && t[1] > t[0] ? [t] : [])].map(([a, b]) => [Math.round(a * 10) / 10, Math.round(b * 10) / 10] as Trecho);
      prontos.current = [];
      if (t) aberto.current = [t[1], t[1]];
      ultimoEnvio.current = Date.now();
      void postar(`/v1/me/academy/aulas/${aulaId}/progresso`, { posicao_segundos: Math.round(segundos), trechos }).catch(() => undefined);
    };
    const andou = (segundos: number) => {
      aoAndar(segundos);
      const t = aberto.current;
      // Seguiu tocando (ate 3 s a frente do fim do trecho): o trecho cresce. Qualquer outro salto fecha e abre outro.
      if (t && segundos >= t[1] - 0.5 && segundos - t[1] <= 3) t[1] = Math.max(t[1], segundos);
      else {
        if (t && t[1] > t[0]) prontos.current.push(t);
        aberto.current = [segundos, segundos];
      }
      if (Date.now() - ultimoEnvio.current >= 15_000) enviar(segundos);
    };
    const ligar = () => {
      const Stream = (window as unknown as { Stream?: (el: HTMLIFrameElement) => typeof player }).Stream;
      if (!Stream || !iframe.current || parar) return;
      player = Stream(iframe.current);
      ultimoEnvio.current = Date.now();
      player?.addEventListener("timeupdate", () => player && andou(player.currentTime));
      player?.addEventListener("ended", () => player && (andou(duracao ?? player.currentTime), enviar(duracao ?? player.currentTime)));
    };
    if ((window as unknown as { Stream?: unknown }).Stream) ligar();
    else {
      const s = document.createElement("script");
      s.src = "https://embed.cloudflarestream.com/embed/sdk.latest.js";
      s.async = true;
      s.onload = ligar;
      document.body.appendChild(s);
    }
    return () => {
      parar = true;
      if (player && player.currentTime > 0) enviar(player.currentTime);
    };
  }, [q.data, aulaId]);

  if (q.isLoading) return <Carregando compacto texto="Abrindo o vídeo" />;
  if (q.isError) return <Aviso tipo="alerta">{erroDe(q.error)}</Aviso>;
  return (
    <div className="mtc-video">
      <iframe
        ref={iframe}
        title="Aula"
        src={`${q.data!.iframe_url}${q.data!.iframe_url.includes("?") ? "&" : "?"}startTime=${Math.max(0, Math.round(inicio))}s`}
        allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
        allowFullScreen
      />
    </div>
  );
}
